// ======================================================================
// File: QAInspectionList.jsx
// Module: PPE → Quality → Incoming QA
// Purpose: Main Inspection List Screen (Supervisor Dashboard Version)
// ======================================================================

import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import incomingQualityService from "../../services/incomingQualityService";

export default function QAInspectionList() {

  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    inspection_no: "",
    grn_no: "",
    vendor: "",
    status: ""
  });

  // -----------------------------------------------------------
  // KPI CALCULATION
  // -----------------------------------------------------------
  const kpis = useMemo(() => {

    const total = items.length;

    const pending =
      items.filter(i => i.status_code === "CREATED").length;

    const inInspection =
      items.filter(i => i.status_code === "IN_INSPECTION").length;

    const accepted =
      items.filter(i => i.status_code === "ACCEPTED").length;

    const rejected =
      items.filter(i => i.status_code === "REJECTED").length;

    return { total, pending, inInspection, accepted, rejected };

  }, [items]);

  // -----------------------------------------------------------
  // STATUS COLOR
  // -----------------------------------------------------------
  const getStatusBadge = (status) => {
    const map = {
      CREATED: "bg-secondary",
      IN_INSPECTION: "bg-primary",
      ACCEPTED: "bg-success",
      PARTIAL_ACCEPTED: "bg-warning",
      REJECTED: "bg-danger"
    };
    return map[status] || "bg-secondary";
  };

  // -----------------------------------------------------------
  // LOAD DATA
  // -----------------------------------------------------------
  const loadData = async (appliedFilters = filters) => {
    try {
      setLoading(true);

      const res =
        await incomingQualityService.getInspectionList(appliedFilters);

      setItems(res.data || []);

    } catch (err) {
      console.error(err);
      toast.error("Failed to load inspections");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // -----------------------------------------------------------
  // FILTER HANDLERS
  // -----------------------------------------------------------
  const handleChange = (e) => {
    setFilters(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const applyFilters = () => loadData(filters);

  const resetFilters = () => {
    const empty = {
      inspection_no: "",
      grn_no: "",
      vendor: "",
      status: ""
    };
    setFilters(empty);
    loadData(empty);
  };

  // -----------------------------------------------------------
  // KPI CARD COMPONENT
  // -----------------------------------------------------------
  const KPICard = ({ title, value, color }) => (
    <div className="col-md-2">
      <div className={`card text-white ${color}`}>
        <div className="card-body py-3">
          <div style={{ fontSize: 13 }}>{title}</div>
          <h4 style={{ margin: 0 }}>{value}</h4>
        </div>
      </div>
    </div>
  );

  // -----------------------------------------------------------
  // UI
  // -----------------------------------------------------------
  return (
    <div>

      {/* HEADER */}
      <div
        style={{
          background:
            "linear-gradient(90deg, #2563eb 0%, #3b82f6 50%, #60a5fa 100%)",
          borderRadius: 12,
          padding: "18px 22px",
          color: "#fff",
          marginBottom: 20,
          boxShadow: "0 8px 20px rgba(0,0,0,0.15)"
        }}
      >
        <h4 style={{ margin: 0, fontWeight: 600 }}>
          Incoming Quality Inspection
        </h4>
        <div style={{ fontSize: 13, opacity: 0.9 }}>
          Inspect GRN received materials before production usage
        </div>
      </div>

      {/* ================= KPI CARDS ================= */}
      <div className="row mb-3 g-2">

        <KPICard title="Total" value={kpis.total} color="bg-dark" />
        <KPICard title="Pending" value={kpis.pending} color="bg-secondary" />
        <KPICard title="In Inspection" value={kpis.inInspection} color="bg-primary" />
        <KPICard title="Accepted" value={kpis.accepted} color="bg-success" />
        <KPICard title="Rejected" value={kpis.rejected} color="bg-danger" />

      </div>

      {/* ================= FILTER BAR ================= */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-2">

            <div className="col-md-2">
              <input name="inspection_no" className="form-control"
                placeholder="Inspection No"
                value={filters.inspection_no}
                onChange={handleChange}/>
            </div>

            <div className="col-md-2">
              <input name="grn_no" className="form-control"
                placeholder="GRN No"
                value={filters.grn_no}
                onChange={handleChange}/>
            </div>

            <div className="col-md-3">
              <input name="vendor" className="form-control"
                placeholder="Vendor"
                value={filters.vendor}
                onChange={handleChange}/>
            </div>

            <div className="col-md-2">
              <select name="status"
                className="form-select"
                value={filters.status}
                onChange={handleChange}>
                <option value="">All Status</option>
                <option value="CREATED">CREATED</option>
                <option value="IN_INSPECTION">IN_INSPECTION</option>
                <option value="ACCEPTED">ACCEPTED</option>
                <option value="PARTIAL_ACCEPTED">PARTIAL_ACCEPTED</option>
                <option value="REJECTED">REJECTED</option>
              </select>
            </div>

            <div className="col-md-3 d-flex gap-2">
              <button className="btn btn-primary" onClick={applyFilters}>
                Search
              </button>
              <button className="btn btn-outline-secondary" onClick={resetFilters}>
                Reset
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* ================= TABLE ================= */}
      <div className="card">
        <div className="card-body p-0">

          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th>Inspection No</th>
                <th>GRN No</th>
                <th>Vendor</th>
                <th>Date</th>
                <th>Status</th>
                <th>Lines</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>

              {loading && (
                <tr>
                  <td colSpan="7" className="text-center">
                    Loading inspections...
                  </td>
                </tr>
              )}

              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center text-muted">
                    No inspections found
                  </td>
                </tr>
              )}

              {!loading && items.map(row => (
                <tr key={row.id}>
                  <td>{row.inspection_no}</td>
                  <td>{row.grn_no || "-"}</td>
                  <td>{row.vendor_name || "-"}</td>
                  <td>{row.inspection_date || "-"}</td>

                  <td>
                    <span className={`badge ${getStatusBadge(row.status_code)}`}>
                      {row.status_code}
                    </span>
                  </td>

                  <td>{row.line_count || 0}</td>

                  <td>
                    <span
                      style={{
                        cursor: "pointer",
                        color: "#2563eb",
                        textDecoration: "underline"
                      }}
                      onClick={() =>
                        navigate(`/ppe/quality/incoming/${row.id}`)
                      }
                    >
                      View
                    </span>
                  </td>
                </tr>
              ))}

            </tbody>
          </table>

        </div>
      </div>

    </div>
  );
}